/**
 * InputManager - Unified input system for desktop and mobile
 * 
 * Provides a single source of truth for player input that works
 * across keyboard (WASD) and mobile (virtual joystick).
 */

import { create } from 'zustand';

// Input state store
export const useInputStore = create((set, get) => ({
  // Movement input (normalized -1 to 1)
  moveX: 0,
  moveZ: 0,
  
  // Camera input (for mobile touch)
  cameraX: 0,
  cameraY: 0,
  
  // Actions
  isSprinting: false,
  interactPressed: false,
  
  // Track active input source
  inputSource: 'none', // 'keyboard', 'touch', 'none'
  
  // Keyboard state (internal)
  _keys: { w: false, a: false, s: false, d: false, shift: false, e: false },
  
  // Update from keyboard
  setKey: (key, pressed) => {
    const state = get();
    const newKeys = { ...state._keys, [key]: pressed };
    
    // Calculate movement from keys
    let moveX = 0, moveZ = 0;
    if (newKeys.w) moveZ -= 1;
    if (newKeys.s) moveZ += 1;
    if (newKeys.a) moveX -= 1;
    if (newKeys.d) moveX += 1;
    
    // Normalize diagonal movement
    const len = Math.sqrt(moveX * moveX + moveZ * moveZ);
    if (len > 1) {
      moveX /= len;
      moveZ /= len;
    }
    
    set({
      _keys: newKeys,
      moveX,
      moveZ,
      isSprinting: newKeys.shift,
      interactPressed: newKeys.e,
      inputSource: 'keyboard',
    });
  },
  
  // Update from mobile joystick (normalized values)
  setJoystick: (x, z) => {
    // Normalize if needed
    const len = Math.sqrt(x * x + z * z);
    if (len > 1) {
      x /= len;
      z /= len;
    }
    
    set({
      moveX: x,
      moveZ: z,
      inputSource: 'touch',
    });
  },
  
  // Update camera from touch drag
  setCamera: (deltaX, deltaY) => {
    set({
      cameraX: deltaX,
      cameraY: deltaY,
    });
  },
  
  // Set sprint from mobile button
  setSprint: (sprinting) => {
    set({ isSprinting: sprinting });
  },
  
  // Set interact from mobile button
  setInteract: (pressed) => {
    set({ interactPressed: pressed });
  },
  
  // Clear all input (on blur, scene change, etc.)
  clearInput: () => {
    set({
      moveX: 0,
      moveZ: 0,
      cameraX: 0,
      cameraY: 0,
      isSprinting: false,
      interactPressed: false,
      inputSource: 'none',
      _keys: { w: false, a: false, s: false, d: false, shift: false, e: false },
    });
  },
}));

// Hook to subscribe keyboard events
export function useKeyboardInput() {
  const setKey = useInputStore(state => state.setKey);
  const clearInput = useInputStore(state => state.clearInput);
  
  return {
    handleKeyDown: (e) => {
      switch (e.code) {
        case 'KeyW': setKey('w', true); break;
        case 'KeyA': setKey('a', true); break;
        case 'KeyS': setKey('s', true); break;
        case 'KeyD': setKey('d', true); break;
        case 'ShiftLeft':
        case 'ShiftRight': setKey('shift', true); break;
        case 'KeyE': setKey('e', true); break;
        default: break;
      }
    },
    handleKeyUp: (e) => {
      switch (e.code) {
        case 'KeyW': setKey('w', false); break;
        case 'KeyA': setKey('a', false); break;
        case 'KeyS': setKey('s', false); break;
        case 'KeyD': setKey('d', false); break;
        case 'ShiftLeft':
        case 'ShiftRight': setKey('shift', false); break;
        case 'KeyE': setKey('e', false); break;
        default: break;
      }
    },
    handleBlur: () => {
      clearInput();
    },
  };
}

export default useInputStore;
